import type { PluginListenerHandle } from "@capacitor/core";

import { MultiplayerBle } from "./index.js";

/// One-to-many BLE broadcast wrappers. Designed for a teaching
/// scenario: one teacher hosts a "channel" advertising a service
/// UUID, multiple students scan + connect + subscribe, and the
/// teacher's `send(payload)` reaches every student at once.
///
/// Distinct from the 1:1 `BleTransport` / `Peer` stack used for
/// Hangman: no handshake, no per-peer state, no heartbeat. Payloads
/// are JSON envelopes the caller defines. The wire format itself
/// is UTF-8 bytes — the same single-frame budget (180 bytes) as
/// the Hangman protocol, since BLE MTU is the same constraint.

/// Service UUID specifically for broadcast. Distinct from the
/// Hangman service UUID so the listener and Hangman guest don't
/// step on each other.
export const BROADCAST_SERVICE_UUID = "5a4f9a40-1b67-4c64-9b6d-3a8b56b3f3f1";
export const BROADCAST_CHAR_UUID = "5a4f9a41-1b67-4c64-9b6d-3a8b56b3f3f1";
/// Unused (broadcast is one-way) but the plugin requires three
/// UUIDs in initialize(). Set to a dummy distinct value so it
/// can't collide with the notify char.
export const BROADCAST_DUMMY_WRITE_UUID = "5a4f9a42-1b67-4c64-9b6d-3a8b56b3f3f1";

const MAX_BROADCAST_BYTES = 180;

export class Broadcaster<TPayload = unknown> {
  private listeners: PluginListenerHandle[] = [];
  private started = false;

  async start(onAudienceChange: (count: number) => void): Promise<void> {
    await MultiplayerBle.initialize({
      role: "host",
      broadcast: true,
      serviceUuid: BROADCAST_SERVICE_UUID,
      hostToGuestUuid: BROADCAST_CHAR_UUID,
      guestToHostUuid: BROADCAST_DUMMY_WRITE_UUID,
    });
    const perm = await MultiplayerBle.requestPermissions();
    if (perm.bluetooth !== "granted") {
      throw new Error("Bluetooth permission denied");
    }
    this.listeners.push(
      await MultiplayerBle.addListener("subscribers", ({ count }) => {
        onAudienceChange(count);
      }),
    );
    await MultiplayerBle.start();
    this.started = true;
  }

  /// Send a payload to every current listener. Returns immediately;
  /// network errors are absorbed (caller can detect "no audience"
  /// via the `onAudienceChange` callback). Throws if the encoded
  /// JSON exceeds 180 bytes — chunking is out of scope for now.
  async send(payload: TPayload): Promise<void> {
    if (!this.started) throw new Error("call start() before send()");
    const json = JSON.stringify(payload);
    const bytes = new TextEncoder().encode(json);
    if (bytes.byteLength > MAX_BROADCAST_BYTES) {
      throw new Error(
        `broadcast payload too big (${bytes.byteLength} > ${MAX_BROADCAST_BYTES} bytes)`,
      );
    }
    await MultiplayerBle.send({ bytes: Array.from(bytes) });
  }

  async stop(): Promise<void> {
    if (!this.started) return;
    this.started = false;
    await MultiplayerBle.stop();
    for (const l of this.listeners) {
      try {
        await l.remove();
      } catch {
        /* listener already gone */
      }
    }
    this.listeners = [];
  }
}

export class Listener<TPayload = unknown> {
  private listeners: PluginListenerHandle[] = [];
  private started = false;

  async start(handlers: {
    onMessage: (payload: TPayload) => void;
    onOpen?: () => void;
    onClose?: (reason?: string) => void;
    onError?: (err: Error) => void;
  }): Promise<void> {
    await MultiplayerBle.initialize({
      role: "guest",
      broadcast: true,
      serviceUuid: BROADCAST_SERVICE_UUID,
      hostToGuestUuid: BROADCAST_CHAR_UUID,
      guestToHostUuid: BROADCAST_DUMMY_WRITE_UUID,
    });
    const perm = await MultiplayerBle.requestPermissions();
    if (perm.bluetooth !== "granted") {
      throw new Error("Bluetooth permission denied");
    }

    this.listeners.push(
      await MultiplayerBle.addListener("opened", () => {
        handlers.onOpen?.();
      }),
    );
    this.listeners.push(
      await MultiplayerBle.addListener("frame", ({ bytes }) => {
        try {
          const json = new TextDecoder().decode(Uint8Array.from(bytes));
          const payload = JSON.parse(json) as TPayload;
          handlers.onMessage(payload);
        } catch (e) {
          handlers.onError?.(e as Error);
        }
      }),
    );
    this.listeners.push(
      await MultiplayerBle.addListener("closed", ({ reason }) => {
        handlers.onClose?.(reason);
      }),
    );
    this.listeners.push(
      await MultiplayerBle.addListener("error", ({ message }) => {
        handlers.onError?.(new Error(message));
      }),
    );

    await MultiplayerBle.start();
    this.started = true;
  }

  async stop(): Promise<void> {
    if (!this.started) return;
    this.started = false;
    await MultiplayerBle.stop();
    for (const l of this.listeners) {
      try {
        await l.remove();
      } catch {
        /* idempotent */
      }
    }
    this.listeners = [];
  }
}
